import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiExternalLink, FiMaximize, FiMinimize, FiRotateCcw, FiRotateCw, FiX, FiZoomIn, FiZoomOut } from "react-icons/fi";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useOcrBusinessUnitScope from "../../hooks/useOcrBusinessUnitScope";
import { loadPdfEngine } from "./pdfRenderer";
import { DEFAULT_VIEW, originalContentPath, clampZoom, displayZoom, normalizeView, viewerPath, viewerScrollGeometry } from "./originalViewerState";
import { originalViewerText } from "./originalViewerMessages";
import "../../PurchasingUi.css";
import "./OriginalDocumentViewer.css";

function PdfPage({ pdf, page, zoom, onSize, onError, loadingLabel }) {
  const surface = useRef(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    let renderTask;
    const host = surface.current;
    const canvas = document.createElement("canvas");
    setLoading(true);
    (async () => {
      const sheet = await pdf.getPage(page);
      if (cancelled) return;
      const base = sheet.getViewport({ scale: 1 });
      onSize({ width: base.width, height: base.height });
      // Bound canvas memory even when a large scanned PDF is zoomed to 800%.
      const quality = Math.min(Math.max(1, zoom * (window.devicePixelRatio || 1)), 4096 / Math.max(base.width, base.height));
      const viewport = sheet.getViewport({ scale: quality });
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      canvas.style.width = `${base.width}px`;
      canvas.style.height = `${base.height}px`;
      renderTask = sheet.render({ canvasContext: canvas.getContext("2d"), viewport });
      await renderTask.promise;
      if (cancelled) return;
      host.replaceChildren(canvas);
      setLoading(false);
    })().catch((error) => {
      if (!cancelled && error?.name !== "RenderingCancelledException") onError();
    });
    return () => { cancelled = true; renderTask?.cancel(); host.replaceChildren(); };
  }, [pdf, page, zoom, onSize, onError]);
  return <><div ref={surface} />{loading && <span className="original-pdf-loading" role="status">{loadingLabel}</span>}</>;
}

export default function OriginalDocumentViewer({ original, initialView = DEFAULT_VIEW, onClose, standalone = false }) {
  const { themeId, languageId } = usePmsTheme();
  const t = (key) => originalViewerText(languageId, key);
  const { api, blocked, businessUnitId, user } = useOcrBusinessUnitScope(true);
  const invalidScope = blocked || businessUnitId !== original.businessUnitId || String(user?.id) !== String(original.userId);
  const contentPath = originalContentPath(original);
  const [view, setView] = useState(() => normalizeView(initialView));
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState({ width: 600, height: 600 });
  const [asset, setAsset] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [retry, setRetry] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [positionRevision, setPositionRevision] = useState(0);
  const root = useRef(null);
  const stage = useRef(null);
  const pointers = useRef(new Map());
  const zoom = displayZoom(view, size, viewport);
  const geometry = viewerScrollGeometry(size, viewport, zoom, view.rotation);
  const ready = Boolean(asset) && !error && !invalidScope;
  const positioned = ready && size.width > 0 && size.height > 0;
  const live = useRef(null);
  live.current = { view, zoom, geometry, positioned, size, viewport };
  const renderError = useCallback(() => setError("The browser could not display this document."), []);
  const onSize = useCallback((next) => setSize((previous) => previous.width === next.width && previous.height === next.height ? previous : next), []);
  const scrollPosition = useCallback(() => {
    const current = live.current;
    if (!current.positioned || !stage.current) return { x: current.view.x, y: current.view.y };
    return {
      x: current.geometry.maxX / 2 - stage.current.scrollLeft,
      y: current.geometry.maxY / 2 - stage.current.scrollTop,
    };
  }, []);
  const recordScroll = useCallback(() => {
    if (!live.current.positioned) return;
    const { x, y } = scrollPosition();
    setView((previous) => previous.x === x && previous.y === y ? previous : { ...previous, x, y });
  }, [scrollPosition]);

  // Restore only after geometry changes; ordinary scroll events must never snap
  // the document back. Waiting for dimensions also preserves detached URL state.
  useLayoutEffect(() => {
    if (!positioned) return;
    const current = live.current;
    stage.current.scrollLeft = Math.max(0, Math.min(current.geometry.maxX, current.geometry.maxX / 2 - current.view.x));
    stage.current.scrollTop = Math.max(0, Math.min(current.geometry.maxY, current.geometry.maxY / 2 - current.view.y));
    recordScroll();
  }, [positioned, geometry.width, geometry.height, geometry.maxX, geometry.maxY, zoom, view.rotation, view.page, view.fit, positionRevision, recordScroll]);

  useEffect(() => {
    if (invalidScope) { setAsset(null); return undefined; }
    let cancelled = false;
    let objectUrl;
    let pdfTask;
    const controller = new AbortController();
    setAsset(null);
    setSize({ width: 0, height: 0 });
    setError("");
    (async () => {
      const response = await api.get(contentPath, {
        responseType: "blob", signal: controller.signal,
      });
      if (cancelled) return;
      const blob = response.data;
      const type = String(blob.type || response.headers?.["content-type"] || "").split(";")[0];
      if (type === "application/pdf") {
        const [engine, buffer] = await Promise.all([loadPdfEngine(), blob.arrayBuffer()]);
        if (cancelled) return;
        pdfTask = engine.getDocument({ data: new Uint8Array(buffer), isEvalSupported: false });
        const pdf = await pdfTask.promise;
        if (cancelled) return;
        setView((previous) => ({ ...previous, page: Math.min(previous.page, pdf.numPages) }));
        setAsset({ pdf });
      } else if (["image/png", "image/jpeg", "image/webp", "image/gif"].includes(type)) {
        objectUrl = URL.createObjectURL(blob);
        setAsset({ url: objectUrl });
      } else {
        throw new Error("Unsupported document format");
      }
    })().catch((reason) => {
      if (!cancelled) setError([403, 404].includes(reason.response?.status)
        ? "This original is no longer available or you do not have access."
        : "Could not load the original. Try again.");
    });
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      pdfTask?.destroy().catch(() => {});
    };
  }, [api, invalidScope, contentPath, retry]);

  useEffect(() => {
    const element = stage.current;
    const measure = () => {
      const nextViewport = { width: element.clientWidth, height: element.clientHeight };
      const current = live.current;
      if (!nextViewport.width || !nextViewport.height
        || (current.viewport.width === nextViewport.width && current.viewport.height === nextViewport.height)) return;
      if (current.positioned) {
        const nextZoom = displayZoom(current.view, current.size, nextViewport);
        const position = scrollPosition();
        const x = position.x * nextZoom / current.zoom;
        const y = current.view.fit === "width" && element.scrollTop <= 1
          ? viewerScrollGeometry(current.size, nextViewport, nextZoom, current.view.rotation).maxY / 2
          : position.y * nextZoom / current.zoom;
        setView((previous) => ({ ...previous, x, y }));
      }
      setViewport(nextViewport);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [scrollPosition]);

  const changeZoom = useCallback((factor) => {
    const current = live.current;
    const next = clampZoom(current.zoom * factor);
    const position = scrollPosition();
    setView((previous) => ({ ...previous, fit: "manual", zoom: next,
      x: position.x * next / current.zoom, y: position.y * next / current.zoom }));
  }, [scrollPosition]);
  const moveScroll = (x, y) => {
    const current = live.current.geometry;
    stage.current.scrollLeft = Math.max(0, Math.min(current.maxX, stage.current.scrollLeft + x));
    stage.current.scrollTop = Math.max(0, Math.min(current.maxY, stage.current.scrollTop + y));
    recordScroll();
  };
  useEffect(() => {
    const changed = () => setFullscreen(document.fullscreenElement === root.current);
    document.addEventListener("fullscreenchange", changed);
    return () => document.removeEventListener("fullscreenchange", changed);
  }, []);

  const rotate = (degrees) => setView((previous) => ({ ...previous, rotation: (previous.rotation + degrees + 360) % 360, x: 0, y: 0 }));
  const fit = (mode) => {
    const next = { ...view, fit: mode, zoom: 1, x: 0, y: 0 };
    if (mode === "width") next.y = viewerScrollGeometry(size, viewport, displayZoom(next, size, viewport), next.rotation).maxY / 2;
    setView(next);
    setPositionRevision((previous) => previous + 1);
  };
  const movePage = (increment) => setView((previous) => ({ ...previous,
    page: Math.max(1, Math.min(asset.pdf.numPages, previous.page + increment)), x: 0, y: 0 }));
  const detach = () => {
    const path = viewerPath(original, { ...view, ...scrollPosition(), fit: "manual", zoom });
    // Keep a handle to detect blocked popups. This is a same-origin Pilcore page;
    // sever its opener immediately, before its asynchronous navigation completes.
    const child = window.open(path, "_blank", "popup,width=1200,height=900");
    if (!child) { setNotice("Allow pop-up windows for Pilcore and try again."); return; }
    child.opener = null;
    onClose?.();
  };
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === root.current) await document.exitFullscreen();
      else if (root.current.requestFullscreen) await root.current.requestFullscreen();
      else setNotice("Full screen is not available in this browser.");
    } catch { setNotice("Full screen is not available in this browser."); }
  };
  const pointerDown = (event) => {
    if (event.target.closest("button")) return;
    if (event.button !== 0 && event.pointerType !== "touch") return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    stage.current.focus({ preventScroll: true });
  };
  const pointerMove = (event) => {
    const old = pointers.current.get(event.pointerId);
    if (!old) return;
    const other = [...pointers.current.entries()].find(([id]) => id !== event.pointerId)?.[1];
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (other) {
      const before = Math.hypot(old.x - other.x, old.y - other.y);
      const after = Math.hypot(event.clientX - other.x, event.clientY - other.y);
      if (before > 0) changeZoom(after / before);
    } else {
      moveScroll(old.x - event.clientX, old.y - event.clientY);
    }
  };
  const pointerUp = (event) => pointers.current.delete(event.pointerId);
  const button = (label, action, icon, disabled = !ready) => <button type="button" title={t(label)} aria-label={t(label)} onClick={action} disabled={disabled}>{icon || t(label)}</button>;

  return <section ref={root} className="original-viewer pilcore-purchasing-ui" data-theme={themeId}
    aria-label={t("Document viewer")} onKeyDown={(event) => {
      event.stopPropagation();
      if (event.key === "Escape" && !fullscreen) onClose?.();
    }}>
    <header className="original-viewer__header">
      <div><span className="original-viewer__brand">PILCORE</span><strong title={original.name}>{original.name || t("Original document")}</strong></div>
      {onClose && button("Close viewer", onClose, <FiX />, false)}
    </header>
    {!standalone && <button className="original-viewer__detach" type="button" onClick={detach} disabled={invalidScope}>
      <FiExternalLink />{t("Open in separate window")}
    </button>}
    <div className="original-viewer__toolbar" role="toolbar" aria-label={t("Document viewer")}>
      {button("Rotate left", () => rotate(-90), <FiRotateCcw />)}
      {button("Rotate right", () => rotate(90), <FiRotateCw />)}
      {button("Zoom out", () => changeZoom(1 / 1.25), <FiZoomOut />, !ready || zoom <= 0.05)}
      <output aria-live="polite">{Math.round(zoom * 100)}%</output>
      {button("Zoom in", () => changeZoom(1.25), <FiZoomIn />, !ready || zoom >= 8)}
      {button("Fit page", () => fit("page"))}
      {button("Fit width", () => fit("width"))}
      {button("100%", () => fit("manual"))}
      {button("Reset view", () => { setView({ ...DEFAULT_VIEW, page: view.page }); setPositionRevision((previous) => previous + 1); })}
      {button(fullscreen ? "Exit full screen" : "Full screen", toggleFullscreen, fullscreen ? <FiMinimize /> : <FiMaximize />, false)}
    </div>
    {asset?.pdf && !invalidScope && <div className="original-viewer__pages">
      {button("Previous page", () => movePage(-1), "‹", !ready || view.page <= 1)}
      <span>{t("Page")} {view.page} {t("of")} {asset.pdf.numPages}</span>
      {button("Next page", () => movePage(1), "›", !ready || view.page >= asset.pdf.numPages)}
    </div>}
    {notice && <div className="original-viewer__notice" role="alert">{t(notice)}</div>}
    <div ref={stage} className="original-viewer__stage" tabIndex={0} aria-label={t("Scroll to move · Use + / − to zoom")}
      onScroll={recordScroll}
      onKeyDown={(event) => {
        const moves = { ArrowLeft: [-40, 0], ArrowRight: [40, 0], ArrowUp: [0, -40], ArrowDown: [0, 40] };
        if (moves[event.key]) { event.preventDefault(); moveScroll(...moves[event.key]); }
        if (event.key === "+" || event.key === "=") { event.preventDefault(); changeZoom(1.25); }
        if (event.key === "-") { event.preventDefault(); changeZoom(1 / 1.25); }
        if (event.key === "0") { event.preventDefault(); fit("page"); }
      }}>
      {invalidScope || error ? <div className="original-viewer__message" role="alert">
        <p>{t(invalidScope ? "Your session or business unit changed. Reopen the original from the correct business unit." : error)}</p>
        {!invalidScope && <button type="button" onClick={() => setRetry((previous) => previous + 1)}>{t("Retry")}</button>}
      </div> : !asset ? <div className="original-viewer__message" role="status">{t("Loading document…")}</div> :
        <div className="original-viewer__plane" style={{ width: geometry.width, height: geometry.height }}
          onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp}
          onPointerCancel={pointerUp} onLostPointerCapture={pointerUp}>
          <div className="original-viewer__sheet" style={{ width: size.width, height: size.height,
            transform: `translate(-50%, -50%) scale(${zoom}) rotate(${view.rotation}deg)` }}>
            {asset.pdf ? <PdfPage pdf={asset.pdf} page={view.page} zoom={zoom} onSize={onSize} onError={renderError} loadingLabel={t("Loading page…")} /> :
              <img src={asset.url} alt={original.name || t("Original document")} draggable={false}
                onLoad={(event) => onSize({ width: event.target.naturalWidth, height: event.target.naturalHeight })} onError={renderError} />}
          </div>
        </div>}
    </div>
    <footer className="original-viewer__footer"><span>{t("Scroll to move · Use + / − to zoom")}</span><span>{t("View only: the original is not changed.")}</span></footer>
  </section>;
}
